import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { freeAgentsService, searchService } from "../../services/api";
import { getPositionDisplay } from "../../utils/positionFormatter";
import { formatCurrencyWithSymbol } from "../../utils/currencyFormatter";
import { resolveNationalities } from "../../utils/nationalityFlags";

// Search-Ranking V1 (Paket C): values match the backend `sort` param on
// /dashboard/free-agents/. relevance = default (best available players first),
// recent = previous behaviour (most recently out of contract first),
// market_value = most expensive first. The backend forces `recent` whenever a
// cursor is sent, so cursor pagination is only used while sort === "recent".
export const DISCOVERY_SORT_VALUES = ["relevance", "recent", "market_value"];
const DEFAULT_SORT = "relevance";

export const useDiscovery = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Utility functions for URL params
  const getFiltersFromURL = () => {
    const params = Object.fromEntries(searchParams);
    return {
      competition: params.competition || "",
      freeAgentSince: params.freeAgentSince || "",
      position: params.position || "",
      gender: params.gender || "male",
      level: params.level || "pro",
      country: params.country || "",
      divisionCountry: params.divisionCountry || "",
      age_min: params.age_min || "",
      age_max: params.age_max || "",
      minMarketValue: params.minMarketValue || "",
      maxMarketValue: params.maxMarketValue || "",
    };
  };

  const getIsFreeAgentFromURL = () => {
    const isFreeAgentParam = searchParams.get("isFreeAgent");
    return isFreeAgentParam !== null ? isFreeAgentParam === "true" : true;
  };

  const getCurrentPageFromURL = () => {
    const page = parseInt(searchParams.get("page"));
    return isNaN(page) || page < 1 ? 1 : page;
  };

  const getSortFromURL = () => {
    const sortParam = searchParams.get("sort");
    return DISCOVERY_SORT_VALUES.includes(sortParam) ? sortParam : DEFAULT_SORT;
  };

  const updateURLParams = (newFilters, newIsFreeAgent, newPage, newSort) => {
    const params = new URLSearchParams();

    // Add filters (omit defaults)
    Object.entries(newFilters).forEach(([key, value]) => {
      if (
        value &&
        value !== "" &&
        !(
          (key === "gender" && value === "male") ||
          (key === "level" && value === "pro")
        )
      ) {
        params.set(key, value);
      }
    });

    // is_free_agent if different from default
    if (newIsFreeAgent !== true) {
      params.set("isFreeAgent", newIsFreeAgent.toString());
    }

    // page if different from default
    if (newPage && newPage !== 1) {
      params.set("page", newPage.toString());
    }

    // sort if different from default (relevance)
    const sortToWrite = newSort || sort;
    if (sortToWrite && sortToWrite !== DEFAULT_SORT) {
      params.set("sort", sortToWrite);
    }

    setSearchParams(params, { replace: true });
  };

  const [filters, setFilters] = useState(getFiltersFromURL);
  const [isFreeAgent, setIsFreeAgent] = useState(getIsFreeAgentFromURL);
  const [sort, setSort] = useState(getSortFromURL);

  // Search states for dropdowns
  const [competitionSearch, setCompetitionSearch] = useState("");
  const [showCompetitionDropdown, setShowCompetitionDropdown] = useState(false);

  const [currentPage, setCurrentPage] = useState(getCurrentPageFromURL);
  const playersPerPage = 25; // Updated to match API default

  // API Data states
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});

  // Filter options
  const [competitions, setCompetitions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [countries, setCountries] = useState([]);

  // Filtered dropdown options
  const [filteredCompetitions, setFilteredCompetitions] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [filteredDivisionCountries, setFilteredDivisionCountries] = useState([]);

  // Search states for dropdowns
  const [countrySearch, setCountrySearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [divisionCountrySearch, setDivisionCountrySearch] = useState("");
  const [showDivisionCountryDropdown, setShowDivisionCountryDropdown] = useState(false);

  // Timer refs for debouncing
  const ageTimerId = useRef(null);
  const marketValueTimerId = useRef(null);
  const requestControllerRef = useRef(null);
  const [cursorStack, setCursorStack] = useState([null]);
  const [nextCursor, setNextCursor] = useState(null);

  // Flag to prevent multiple initial fetches
  const initialFetchDone = useRef(false);
  const isInitialMount = useRef(true);

  // Utility functions
  // Market value on the Discovery cards.
  //
  // This was the only de-DE formatter in the repo: it printed "1.000.000 €"
  // — dot grouping and a TRAILING symbol — while every other surface on the
  // platform printed "€1,000,000". Two spellings of one number, decided by
  // nothing but which file you were looking at.
  //
  // It now uses the shared formatter like everything else: "€1'000'000"
  // (Marin, 2026-08-02: "für jede Währung Apostrophen ' verwenden"). EUR stays
  // hardcoded — this endpoint returns market values in Euro only, and that is
  // not this change's question.
  const formatMarketValue = (value) => {
    if (!value) return "N/A";
    return formatCurrencyWithSymbol(value, "EUR");
  };

  // Transform API data to UI format
  const transformPlayerData = useCallback((apiPlayers) => {
    return apiPlayers.map((player) => {
      const detailedPositions =
        player.tp_detailed_position ||
        player.tpDetailedPosition ||
        player.tpDetailedPositions ||
        null;
      const fallbackPosition = player.position || "-";
      const positionDisplay = getPositionDisplay(player, fallbackPosition);

      return {
        id: player.wyscout_id,
        wyscout_id: player.wyscout_id,
        sportmonks_id: player.sportmonks_id || player.wyscout_id, // Fallback for navigation
        name: player.full_name || player.name || player.short_name,
        position: fallbackPosition,
        positionDisplay,
        tp_detailed_position: detailedPositions,
        position_code2: player.position_code2,
        position_code3: player.position_code3,
        club: {
          id:
            player.club?.id ||
            player.club_id ||
            player.clubId ||
            player.club?.club_id ||
            null,
          name: player.club?.name || "Free Agent",
          logo: player.club_image || player.club?.logo || null,
        },
        nationality: player.nationality?.name || "Unknown",
        nationalityId: player.nationality?.id,
        countryCode: player.nationality?.code || "unknown",
        age: player.age,
        date_of_birth: player.date_of_birth,
        marketValue: formatMarketValue(player.market_value),
        market_currency: player.market_currency,
        freeAgentDate: player.free_since || "N/A",
        image_url: player.image_url || player.profile_image || null,
        nationality_image: player.nationality?.image_url,
        // All citizenships in priority order (national team first). The API
        // sends `nationalities`; resolveNationalities falls back to the
        // nationality/birth_country pair for older payloads.
        nationalities: resolveNationalities(player),
        gender: player.gender,
        status: player.status,
        source: player.source,
        birthCountryImage: player.birth_country?.image_url || null,
      };
    });
  }, []);

  // Load filter options
  const loadFilterOptions = async () => {
    try {
      const [positionsRes, countriesRes] = await Promise.all([
        freeAgentsService.getPositions(),
        freeAgentsService.getCountries(),
      ]);

      if (positionsRes.success) {
        setPositions(positionsRes.data);
      }
      if (countriesRes.success) {
        setCountries(countriesRes.data);
        setFilteredCountries(countriesRes.data);
      }

      // Don't load competitions initially - they'll be loaded when country is selected
      setCompetitions([]);
      setFilteredCompetitions([]);
    } catch (error) {
      console.error("Error loading filter options:", error);
    }
  };

  // Filter countries based on search (synchronous, no useEffect needed)
  useEffect(() => {
    if (countrySearch) {
      const search = (countrySearch || "").toLowerCase();
      const filtered = countries.filter((country) => {
        const label = ((country && country.name) || "")
          .toString()
          .toLowerCase();
        return label.includes(search);
      });
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries);
    }
  }, [countrySearch, countries]);

  // Filter division countries based on search
  useEffect(() => {
    if (divisionCountrySearch) {
      const search = (divisionCountrySearch || "").toLowerCase();
      const filtered = countries.filter((country) => {
        const label = ((country && country.name) || "")
          .toString()
          .toLowerCase();
        return label.includes(search);
      });
      setFilteredDivisionCountries(filtered);
    } else {
      setFilteredDivisionCountries(countries);
    }
  }, [divisionCountrySearch, countries]);

  // Filter competitions based on search (synchronous, no useEffect needed)
  useEffect(() => {
    if (competitionSearch) {
      const search = (competitionSearch || "").toLowerCase();
      const filtered = competitions.filter((comp) => {
        const label = ((comp && (comp.name || comp.title)) || "")
          .toString()
          .toLowerCase();
        return label.includes(search);
      });
      setFilteredCompetitions(filtered);
    } else {
      setFilteredCompetitions(competitions);
    }
  }, [competitionSearch, competitions]);

  // Handle filter changes with direct API calls (no useEffect needed!)
  const handleLevelChange = (value) => {
    const newFilters = { ...filters, level: value };
    setFilters(newFilters);
    setCurrentPage(1);
    setCursorStack([null]);
    updateURLParams(newFilters, isFreeAgent, 1);
    // Pass the new filters directly to avoid stale state
    fetchData(1, null, newFilters);
  };

  const handlePositionChange = (value) => {
    const newFilters = { ...filters, position: value };
    setFilters(newFilters);
    setCurrentPage(1);
    setCursorStack([null]);
    updateURLParams(newFilters, isFreeAgent, 1);
    fetchData(1, null, newFilters);
  };

  const handleFreeAgentSinceChange = (value) => {
    const newFilters = { ...filters, freeAgentSince: value };
    setFilters(newFilters);
    setCurrentPage(1);
    setCursorStack([null]);
    updateURLParams(newFilters, isFreeAgent, 1);
    fetchData(1, null, newFilters);
  };

  const handleFreeAgentToggle = () => {
    const newIsFreeAgent = !isFreeAgent;
    setIsFreeAgent(newIsFreeAgent);
    setCurrentPage(1);
    setCursorStack([null]);
    updateURLParams(filters, newIsFreeAgent, 1);
    fetchData(1, null, null, newIsFreeAgent);
  };

  // Sort change (Search-Ranking V1): reset to page 1, drop the cursor stack
  // (cursors belong to the previous ordering) and refetch with the new sort.
  const handleSortChange = (value) => {
    const newSort = DISCOVERY_SORT_VALUES.includes(value) ? value : DEFAULT_SORT;
    if (newSort === sort) return;
    setSort(newSort);
    setCurrentPage(1);
    setCursorStack([null]);
    updateURLParams(filters, isFreeAgent, 1, newSort);
    fetchData(1, null, null, null, newSort);
  };

  // Handle country selection (Player Nationality)
  const handleCountrySelect = async (country) => {
    if (!country) return;

    const countryCode =
      country.alpha3code ||
      country.alpha3 ||
      country.code ||
      country.alpha2 ||
      country.iso2 ||
      country.code3;

    const normalizedCode = countryCode ? String(countryCode).toUpperCase() : "";

    const newFilters = {
      ...filters,
      country: normalizedCode,
    };
    
    setFilters(newFilters);
    setCountrySearch(country.name || "");
    setShowCountryDropdown(false);

    // Fetch data with new country filter (area parameter)
    setCurrentPage(1);
    setCursorStack([null]);
    updateURLParams(newFilters, isFreeAgent, 1);
    fetchData(1, null, newFilters);
  };

  // Handle division country selection
  const handleDivisionCountrySelect = async (country) => {
    if (!country) return;

    const countryCode =
      country.alpha3code ||
      country.alpha3 ||
      country.code ||
      country.alpha2 ||
      country.iso2 ||
      country.code3;

    const normalizedCode = countryCode ? String(countryCode).toUpperCase() : "";

    const newFilters = {
      ...filters,
      divisionCountry: normalizedCode,
      competition: "", // Clear competition when division country changes
    };
    
    setFilters(newFilters);
    setDivisionCountrySearch(country.name || "");
    setCompetitionSearch("");
    setShowDivisionCountryDropdown(false);

    // Load competitions for selected division country
    if (normalizedCode) {
      try {
        const competitionsRes = await searchService.getLeagues(normalizedCode);
        if (competitionsRes.success) {
          setCompetitions(competitionsRes.data);
          setFilteredCompetitions(competitionsRes.data);
        }
      } catch (error) {
        console.error("Error loading competitions for country:", error);
      }
    } else {
      setCompetitions([]);
      setFilteredCompetitions([]);
    }

    // Do NOT fetch data here - only load leagues
  };

  // Handle competition selection
  const handleCompetitionSelect = (competition) => {
    const newFilters = { ...filters, competition: competition.id };
    setFilters(newFilters);
    setCompetitionSearch(competition.name || competition.title);
    setShowCompetitionDropdown(false);

    // Fetch data with new competition filter
    setCurrentPage(1);
    setCursorStack([null]);
    updateURLParams(newFilters, isFreeAgent, 1);
    fetchData(1, null, newFilters);
  };

  // Get selected country name for display (Player Nationality)
  const getSelectedCountryName = () => {
    if (!filters.country) return "";
    const selected = countries.find((c) => {
      const code =
        c?.alpha3code ||
        c?.alpha3 ||
        c?.code ||
        c?.alpha2 ||
        c?.iso2 ||
        c?.code3;

      return code && String(code).toUpperCase() === filters.country;
    });
    return selected?.name || "";
  };

  // Get selected division country name for display
  const getSelectedDivisionCountryName = () => {
    if (!filters.divisionCountry) return "";
    const selected = countries.find((c) => {
      const code =
        c?.alpha3code ||
        c?.alpha3 ||
        c?.code ||
        c?.alpha2 ||
        c?.iso2 ||
        c?.code3;

      return code && String(code).toUpperCase() === filters.divisionCountry;
    });
    return selected?.name || "";
  };

  // Get selected competition name for display
  const getSelectedCompetitionName = () => {
    if (!filters.competition) return "";
    const selected = competitions.find(
      (c) => String(c.id) === String(filters.competition)
    );
    return selected?.name || selected?.title || "";
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    const clearedFilters = {
      competition: "",
      freeAgentSince: "",
      position: "",
      gender: "male",
      level: "pro",
      country: "",
      divisionCountry: "",
      age_min: "",
      age_max: "",
      minMarketValue: "",
      maxMarketValue: "",
    };
    
    setFilters(clearedFilters);
    setCompetitionSearch("");
    setCountrySearch("");
    setDivisionCountrySearch("");
    setCompetitions([]);
    setFilteredCompetitions([]);

    // Fetch data with cleared filters
    setCurrentPage(1);
    setCursorStack([null]);
    updateURLParams(clearedFilters, true, 1);
    fetchData(1, null, clearedFilters);
  };

  // Clear individual filters
  const handleClearFilter = (filterKey) => {
    const newFilters = { ...filters, [filterKey]: "" };
    setFilters(newFilters);
    
    if (filterKey === "country") {
      setCountrySearch("");
    } else if (filterKey === "divisionCountry") {
      setDivisionCountrySearch("");
      setCompetitions([]);
      setFilteredCompetitions([]);
    } else if (filterKey === "competition") {
      setCompetitionSearch("");
    }

    // Fetch data after clearing filter
    setCurrentPage(1);
    setCursorStack([null]);
    updateURLParams(newFilters, isFreeAgent, 1);
    fetchData(1, null, newFilters);
  };

  // Check if any filters are active
  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === "gender" && value === "male") return false; // default
    if (key === "level" && value === "pro") return false; // default
    return value !== "" && value !== null && value !== undefined;
  });

  // Single fetch function with optional override parameters
  const fetchData = async (page = 1, cursorArg = null, overrideFilters = null, overrideIsFreeAgent = null, overrideSort = null) => {
    // cancel previous
    if (requestControllerRef.current) {
      requestControllerRef.current.abort();
    }
    const controller = new AbortController();
    requestControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      // Use override values if provided, otherwise use state
      const currentFilters = overrideFilters || filters;
      const currentIsFreeAgent = overrideIsFreeAgent !== null ? overrideIsFreeAgent : isFreeAgent;
      const currentSort = overrideSort || sort;

      const params = {
        is_free_agent: currentIsFreeAgent,
        sort: currentSort,
      };

      // The backend forces sort=recent as soon as a cursor is present, so a
      // cursor may only be sent while sorting by "recent"; the other sorts
      // always paginate by page number (paginate() gates this too).
      if (cursorArg && currentSort === "recent") {
        params.cursor = cursorArg;
        params.per_page = playersPerPage;
      } else {
        params.page = page;
        params.per_page = playersPerPage;
      }

      // Add new API filters
      if (currentFilters.gender) {
        params.gender = currentFilters.gender;
      }
      if (currentFilters.level) {
        params.level = currentFilters.level;
      }
      if (currentFilters.country) {
        params.area = currentFilters.country;
      }
      if (currentFilters.competition) {
        params.competition_ids = currentFilters.competition;
      }
      if (currentFilters.position) {
        params.position = currentFilters.position; // Use Wyscout position codes
      }
      if (currentFilters.age_min && currentFilters.age_min.trim() !== "") {
        const ageMin = parseInt(currentFilters.age_min);
        if (!isNaN(ageMin) && ageMin >= 16) {
          params.age_min = ageMin;
        }
      }
      if (currentFilters.age_max && currentFilters.age_max.trim() !== "") {
        const ageMax = parseInt(currentFilters.age_max);
        if (!isNaN(ageMax) && ageMax <= 45) {
          params.age_max = ageMax;
        }
      }
      if (currentFilters.minMarketValue && currentFilters.minMarketValue.trim() !== "") {
        const minValue = parseInt(
          currentFilters.minMarketValue.replace(/[€,\s]/g, "")
        );
        if (!isNaN(minValue) && minValue > 0) {
          params.market_value_min = minValue;
        }
      }
      if (currentFilters.maxMarketValue && currentFilters.maxMarketValue.trim() !== "") {
        const maxValue = parseInt(
          currentFilters.maxMarketValue.replace(/[€,\s]/g, "")
        );
        if (!isNaN(maxValue) && maxValue > 0) {
          params.market_value_max = maxValue;
        }
      }

      // Handle free agent since filter - convert to recently_expired months
      if (currentFilters.freeAgentSince) {
        switch (currentFilters.freeAgentSince) {
          case "1-month":
            params.recently_expired = 1;
            break;
          case "3-months":
            params.recently_expired = 3;
            break;
          case "6-months":
            params.recently_expired = 6;
            break;
          default:
            params.recently_expired = 3; // Default
        }
      }

      const result = await freeAgentsService.getFreeAgents(
        params,
        controller.signal
      );

      // Ignore outdated responses
      if (requestControllerRef.current !== controller) return;

      if (result.success) {
        const transformedPlayers = transformPlayerData(result.data);
        setPlayers(transformedPlayers);
        console.log(transformedPlayers);
        setPagination(result.pagination || {});
        setNextCursor(result.pagination?.next_cursor || null);
      } else {
        setError(result.error);
        setPlayers([]);
      }
    } catch (error) {
      if (error?.name === "CanceledError") return;
      console.error("Error loading free agents:", error);
      setError("Failed to load free agents");
      setPlayers([]);
    } finally {
      // Only clear loading for the latest controller
      if (requestControllerRef.current === controller && !controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  // 1. Initial fetch - ONLY load countries and fetch free agents (2 API calls only!)
  useEffect(() => {
    const initialize = async () => {
      // Load filter options (positions and countries)
      await loadFilterOptions();
      // Fetch initial free agents data using page from URL (restores page position)
      const initialPage = getCurrentPageFromURL();
      fetchData(initialPage);
      updateURLParams(getFiltersFromURL(), getIsFreeAgentFromURL(), initialPage);
      initialFetchDone.current = true;
      isInitialMount.current = false;
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Age filters with debounce - ONLY after initial mount
  useEffect(() => {
    if (isInitialMount.current) return; // Skip on initial mount

    // Clear existing timeout
    if (ageTimerId.current) {
      clearTimeout(ageTimerId.current);
    }

    // Set new timeout
    ageTimerId.current = setTimeout(() => {
      setCurrentPage(1);
      setCursorStack([null]);
      updateURLParams(filters, isFreeAgent, 1);
      // Pass current filters directly
      fetchData(1, null, filters);
    }, 1000);

    // Cleanup on unmount
    return () => {
      if (ageTimerId.current) {
        clearTimeout(ageTimerId.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.age_min, filters.age_max]);

  // 3. Market value filters with debounce - ONLY after initial mount
  useEffect(() => {
    if (isInitialMount.current) return; // Skip on initial mount

    // Clear existing timeout
    if (marketValueTimerId.current) {
      clearTimeout(marketValueTimerId.current);
    }

    // Set new timeout
    marketValueTimerId.current = setTimeout(() => {
      setCurrentPage(1);
      setCursorStack([null]);
      updateURLParams(filters, isFreeAgent, 1);
      // Pass current filters directly
      fetchData(1, null, filters);
    }, 1000);

    // Cleanup on unmount
    return () => {
      if (marketValueTimerId.current) {
        clearTimeout(marketValueTimerId.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.minMarketValue, filters.maxMarketValue]);

  // Change page
  const paginate = (pageNumber) => {
    // Clear any pending debounced filter timers to avoid resetting back to page 1
    if (ageTimerId.current) {
      clearTimeout(ageTimerId.current);
    }
    if (marketValueTimerId.current) {
      clearTimeout(marketValueTimerId.current);
    }
    if (pageNumber === currentPage) {
      // same page refresh (ensure URL sync)
      setCurrentPage(pageNumber);
      updateURLParams(filters, isFreeAgent, pageNumber);
      fetchData(pageNumber);
      return;
    }
    if (pageNumber === currentPage + 1) {
      if (pagination?.has_next) {
        // Prefer cursor if available, fallback to page number. Cursors force
        // sort=recent on the backend, so they are only usable for that sort.
        if (nextCursor && sort === "recent") {
          setCursorStack((prev) => [...prev, nextCursor]);
          setCurrentPage(pageNumber);
          updateURLParams(filters, isFreeAgent, pageNumber);
          fetchData(undefined, nextCursor);
        } else {
          setCurrentPage(pageNumber);
          updateURLParams(filters, isFreeAgent, pageNumber);
          fetchData(pageNumber);
        }
      }
      return;
    }
    if (pageNumber === currentPage - 1) {
      if (pagination?.has_prev) {
        // Prefer cursor stack if available, fallback to page number (cursors
        // are only valid for sort=recent, see above)
        if (cursorStack.length > 1 && sort === "recent") {
          const newStack = cursorStack.slice(0, -1);
          const prevCursor = newStack[newStack.length - 1];
          setCursorStack(newStack);
          setCurrentPage(pageNumber);
          updateURLParams(filters, isFreeAgent, pageNumber);
          fetchData(undefined, prevCursor);
        } else {
          setCurrentPage(pageNumber);
          updateURLParams(filters, isFreeAgent, pageNumber);
          fetchData(pageNumber);
        }
      }
      return;
    }
    // absolute jump (direct page fetch)
    setCursorStack([null]);
    setCurrentPage(pageNumber);
    updateURLParams(filters, isFreeAgent, pageNumber);
    fetchData(pageNumber);
  };

  // abort on unmount
  useEffect(() => {
    return () => {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // State
    filters,
    setFilters,
    isFreeAgent,
    setIsFreeAgent,
    sort,
    competitionSearch,
    setCompetitionSearch,
    showCompetitionDropdown,
    setShowCompetitionDropdown,
    currentPage,
    players,
    loading,
    error,
    pagination,
    competitions,
    positions,
    countries,
    filteredCompetitions,
    filteredCountries,
    filteredDivisionCountries,
    countrySearch,
    setCountrySearch,
    showCountryDropdown,
    setShowCountryDropdown,
    divisionCountrySearch,
    setDivisionCountrySearch,
    showDivisionCountryDropdown,
    setShowDivisionCountryDropdown,
    nextCursor,
    
    // Functions
    handleCountrySelect,
    handleDivisionCountrySelect,
    handleCompetitionSelect,
    getSelectedCountryName,
    getSelectedDivisionCountryName,
    getSelectedCompetitionName,
    handleClearAllFilters,
    handleClearFilter,
    hasActiveFilters,
    fetchData,
    paginate,
    handleLevelChange,
    handlePositionChange,
    handleFreeAgentSinceChange,
    handleFreeAgentToggle,
    handleSortChange,
  };
};
